import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface InviteInfo {
  email: string;
  name: string;
  clinic_name: string;
  role: string;
}

export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');

  const [state, setState] = useState<'loading' | 'valid' | 'invalid' | 'submitting' | 'success'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      setErrorMessage('No invite token provided.');
      return;
    }
    validateToken();
  }, [token]);

  async function validateToken() {
    try {
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: { token, validate_only: true },
      });

      if (error || !data?.valid) {
        setState('invalid');
        setErrorMessage(data?.error || 'This invite link is invalid or has expired.');
        return;
      }

      setInvite(data as InviteInfo);
      setFullName(data.name || '');
      setState('valid');
    } catch {
      setState('invalid');
      setErrorMessage('Unable to validate invite link.');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!fullName.trim()) {
      toast({ title: 'Please enter your full name', variant: 'destructive' });
      return;
    }
    if (password.length < 8) {
      toast({ title: 'Password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    setState('submitting');

    try {
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: { token, password, full_name: fullName.trim() },
      });

      if (error || !data?.success) {
        toast({ title: data?.error || 'Failed to create account', variant: 'destructive' });
        setState('valid');
        return;
      }

      // Sign in directly on the client using the credentials we already have
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invite.email,
        password,
      });

      if (signInError) {
        console.error('Auto sign-in failed:', signInError);
        setState('success');
        toast({ title: 'Account created! Please sign in.' });
        setTimeout(() => navigate('/auth', { replace: true }), 2000);
      } else {
        setState('success');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
      }
    } catch {
      toast({ title: 'Something went wrong. Please try again.', variant: 'destructive' });
      setState('valid');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="mb-4">
            <span className="font-['Source_Serif_4'] font-black text-2xl text-foreground tracking-tight">Veracord</span>
            <span className="text-[7px] text-foreground align-super ml-0.5">™</span>
          </div>
        </CardHeader>
        <CardContent>
          {state === 'loading' && (
            <div className="flex flex-col items-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Validating your invite...</p>
            </div>
          )}

          {state === 'invalid' && (
            <div className="flex flex-col items-center py-8 gap-4 text-center">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Invalid Invite</h2>
                <p className="text-sm text-muted-foreground">
                  {errorMessage || 'This invite link has expired or is invalid. Please ask your clinic admin to resend your invite.'}
                </p>
              </div>
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center py-8 gap-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Welcome to Veracord!</h2>
                <p className="text-sm text-muted-foreground">Redirecting you to your dashboard...</p>
              </div>
            </div>
          )}

          {(state === 'valid' || state === 'submitting') && invite && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="text-center mb-2">
                <h2 className="text-lg font-semibold text-foreground">Set Up Your Account</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  You've been invited to join <strong>{invite.clinic_name}</strong>
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="join-email">Email</Label>
                <Input id="join-email" value={invite.email} disabled className="bg-muted" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="join-name">Full Name</Label>
                <Input
                  id="join-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  required
                  disabled={state === 'submitting'}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="join-password">Password</Label>
                <Input
                  id="join-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  disabled={state === 'submitting'}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="join-confirm">Confirm Password</Label>
                <Input
                  id="join-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  disabled={state === 'submitting'}
                />
              </div>

              <Button type="submit" className="w-full" disabled={state === 'submitting'}>
                {state === 'submitting' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Account...</>
                ) : (
                  'Create Account & Join'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
